import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { leaveRequestAPI, leaveTypeAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { StatusPill } from '@/components/StatusPill';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Check, X, FileText, AlertTriangle, Shield } from 'lucide-react';

const statusLabels = {
  pending: 'Pending Manager',
  pending_super_user: 'Pending Super User',
  approved: 'Approved',
  rejected: 'Rejected',
};

const statusColors = {
  pending: 'pending',
  pending_super_user: 'late',
  approved: 'approved',
  rejected: 'rejected',
};

export default function LeavesPage() {
  const { user, isAdmin, isStaff, isSuperUser, isManager } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balance, setBalance] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionComment, setActionComment] = useState('');
  const [form, setForm] = useState({
    leave_type_id: '', start_date: '', end_date: '', reason: '', is_half_day: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [reqRes, branchRes] = await Promise.all([
        leaveRequestAPI.list({}),
        branchAPI.list()
      ]);
      setLeaveRequests(reqRes.data);
      setBranches(branchRes.data);

      const branchId = user?.branch_id || branchRes.data[0]?.id;
      if (branchId) {
        const ltRes = await leaveTypeAPI.list(branchId);
        setLeaveTypes(ltRes.data);
      }

      if (user?.id) {
        try {
          const balRes = await leaveRequestAPI.balance(user.id);
          setBalance(balRes.data);
        } catch (e) { console.error(e); }
      }
    } catch { toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  };

  const handleRequestLeave = async () => {
    if (!form.leave_type_id || !form.start_date) {
      toast.error('Leave type and start date are required');
      return;
    }
    if (!form.end_date) form.end_date = form.start_date;
    try {
      await leaveRequestAPI.create(form);
      toast.success('Leave request submitted');
      setRequestDialogOpen(false);
      setForm({ leave_type_id: '', start_date: '', end_date: '', reason: '', is_half_day: false });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleManagerAction = async (requestId, status) => {
    try {
      await leaveRequestAPI.managerAction(requestId, { status, comment: actionComment });
      toast.success(`Leave ${status} by manager`);
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionComment('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleSuperUserAction = async (requestId, status) => {
    try {
      await leaveRequestAPI.superUserAction(requestId, { status, comment: actionComment });
      toast.success(`Leave ${status} by super user`);
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionComment('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const pendingManager = leaveRequests.filter(r => r.status === 'pending');
  const pendingSuperUser = leaveRequests.filter(r => r.status === 'pending_super_user');
  const myRequests = leaveRequests.filter(r => r.employee_id === user?.id);

  const getDefaultTab = () => {
    if (isSuperUser && pendingSuperUser.length > 0) return 'super-user-review';
    if (isManager && pendingManager.length > 0) return 'pending-manager';
    return 'my-requests';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Leave Management</h1>
          <p className="text-muted-foreground">Request, track, and manage leaves</p>
        </div>
        <Button onClick={() => setRequestDialogOpen(true)} className="gap-2" data-testid="request-leave-button">
          <Plus size={16} /> Request Leave
        </Button>
      </div>

      {/* Leave Balance */}
      {balance && balance.balances?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {balance.balances.map(b => (
            <Card key={b.leave_type_id}>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">{b.leave_type_name}</p>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-2xl font-heading font-semibold tabular-nums">{b.remaining_days}</span>
                  <span className="text-sm text-muted-foreground">/ {b.total_days}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{b.used_days} used</p>
              </CardContent>
            </Card>
          ))}
          {!balance.is_entitled && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm font-medium text-amber-700">Probation Period</p>
                <p className="text-xs text-amber-600 mt-1">Starts from {balance.entitlement_start_date}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Tabs defaultValue={getDefaultTab()}>
        <TabsList className="flex-wrap">
          {isSuperUser && (
            <TabsTrigger value="super-user-review" data-testid="super-user-review-tab">
              <Shield size={14} className="mr-1.5" />
              Super User Review
              {pendingSuperUser.length > 0 && (
                <span className="ml-1.5 bg-destructive text-destructive-foreground text-xs rounded-full px-1.5 py-0.5">{pendingSuperUser.length}</span>
              )}
            </TabsTrigger>
          )}
          {isManager && (
            <TabsTrigger value="pending-manager">
              Pending Approval
              {pendingManager.length > 0 && (
                <span className="ml-1.5 bg-destructive text-destructive-foreground text-xs rounded-full px-1.5 py-0.5">{pendingManager.length}</span>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="my-requests">My Requests</TabsTrigger>
          {isAdmin && <TabsTrigger value="all">All Requests</TabsTrigger>}
        </TabsList>

        {/* Super User Review Tab */}
        {isSuperUser && (
          <TabsContent value="super-user-review" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Shield size={18} className="text-primary" />
                  Escalated Leaves — Awaiting Your Decision
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {pendingSuperUser.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Shield className="mx-auto mb-3 text-muted-foreground/50" size={40} />
                    <p>No leaves pending your review</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Manager Decision</TableHead>
                        <TableHead>Escalation Reason</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingSuperUser.map(req => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.leave_type_name}</TableCell>
                          <TableCell className="tabular-nums">{req.start_date} to {req.end_date}</TableCell>
                          <TableCell className="tabular-nums">{req.days}</TableCell>
                          <TableCell>
                            {req.manager_action ? (
                              <Badge variant={req.manager_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">
                                {req.manager_action}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">Direct to SU</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[200px]">
                            {req.escalation_reason === 'manager_leave' && 'Manager leave'}
                            {req.escalation_reason === 'manager_rejected_review' && 'Manager rejected — review'}
                            {req.escalation_reason && !['manager_leave', 'manager_rejected_review'].includes(req.escalation_reason) && req.escalation_reason}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-8 gap-1" onClick={() => { setSelectedRequest(req); setActionDialogOpen(true); }}>
                                <Check size={14} /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 gap-1 text-destructive" onClick={() => handleSuperUserAction(req.id, 'rejected')}>
                                <X size={14} /> Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Manager Pending Tab */}
        {isManager && (
          <TabsContent value="pending-manager" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {pendingManager.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">No pending approvals</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Dates</TableHead>
                        <TableHead>Days</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingManager.map(req => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.employee_name}</TableCell>
                          <TableCell>{req.leave_type_name}</TableCell>
                          <TableCell className="tabular-nums">{req.start_date} to {req.end_date}</TableCell>
                          <TableCell className="tabular-nums">{req.days}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{req.reason || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="sm" className="h-8 gap-1" onClick={() => { setSelectedRequest(req); setActionDialogOpen(true); }}>
                                <Check size={14} /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 gap-1 text-destructive" onClick={() => handleManagerAction(req.id, 'rejected')}>
                                <X size={14} /> Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* My Requests */}
        <TabsContent value="my-requests" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {myRequests.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No leave requests yet</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead>Super User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {myRequests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell>{req.leave_type_name}</TableCell>
                        <TableCell className="tabular-nums">{req.start_date} to {req.end_date}</TableCell>
                        <TableCell className="tabular-nums">{req.days}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{req.reason || '-'}</TableCell>
                        <TableCell>
                          <StatusPill status={statusColors[req.status] || 'pending'}>
                            {statusLabels[req.status] || req.status}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="text-xs">
                          {req.manager_action ? (
                            <Badge variant={req.manager_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">
                              {req.manager_action}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {req.super_user_action ? (
                            <Badge variant={req.super_user_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">
                              {req.super_user_action}
                            </Badge>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Requests */}
        {isAdmin && (
          <TabsContent value="all" className="mt-4">
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Manager</TableHead>
                      <TableHead>Super User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leaveRequests.map(req => (
                      <TableRow key={req.id}>
                        <TableCell className="font-medium">{req.employee_name}</TableCell>
                        <TableCell>{req.leave_type_name}</TableCell>
                        <TableCell className="tabular-nums">{req.start_date} to {req.end_date}</TableCell>
                        <TableCell className="tabular-nums">{req.days}</TableCell>
                        <TableCell>
                          <StatusPill status={statusColors[req.status] || 'pending'}>
                            {statusLabels[req.status] || req.status}
                          </StatusPill>
                        </TableCell>
                        <TableCell className="text-xs">
                          {req.manager_action && (
                            <Badge variant={req.manager_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">
                              {req.manager_action}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {req.super_user_action && (
                            <Badge variant={req.super_user_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">
                              {req.super_user_action}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Request Leave Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Request Leave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.leave_type_id} onChange={e => setForm({...form, leave_type_id: e.target.value})} data-testid="leave-type-select">
                <option value="">Select leave type</option>
                {leaveTypes.map(lt => <option key={lt.id} value={lt.id}>{lt.name} ({lt.total_days} days)</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="leave-start-date" />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="leave-end-date" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_half_day} onCheckedChange={v => setForm({...form, is_half_day: v})} />
              <Label>Half Day</Label>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Reason for leave..." data-testid="leave-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestLeave} data-testid="leave-request-submit-button">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {isSuperUser ? 'Super User Approval' : 'Approve Leave Request'}
            </DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p><strong>Type:</strong> {selectedRequest.leave_type_name}</p>
                <p><strong>Dates:</strong> {selectedRequest.start_date} to {selectedRequest.end_date} ({selectedRequest.days} days)</p>
                <p><strong>Reason:</strong> {selectedRequest.reason || 'N/A'}</p>
                {selectedRequest.manager_action && (
                  <p><strong>Manager Decision:</strong> <Badge variant={selectedRequest.manager_action === 'approved' ? 'default' : 'destructive'} className="text-xs capitalize">{selectedRequest.manager_action}</Badge></p>
                )}
                {selectedRequest.manager_comment && (
                  <p><strong>Manager Comment:</strong> {selectedRequest.manager_comment}</p>
                )}
                {selectedRequest.escalation_reason && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2 mt-2">
                    <p className="text-xs text-amber-700 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      Escalation: {selectedRequest.escalation_reason}
                    </p>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Comment (optional)</Label>
                <Textarea value={actionComment} onChange={e => setActionComment(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialogOpen(false);
                if (selectedRequest) {
                  if (isSuperUser && selectedRequest.status === 'pending_super_user') {
                    handleSuperUserAction(selectedRequest.id, 'rejected');
                  } else {
                    handleManagerAction(selectedRequest.id, 'rejected');
                  }
                }
              }}
              className="text-destructive"
            >
              Reject
            </Button>
            <Button onClick={() => {
              if (selectedRequest) {
                if (isSuperUser && selectedRequest.status === 'pending_super_user') {
                  handleSuperUserAction(selectedRequest.id, 'approved');
                } else {
                  handleManagerAction(selectedRequest.id, 'approved');
                }
              }
            }}>
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
